import React from 'react';
import {
  ExIconButton, IconButtonFlavor, IconButtonSize, IconButtonType,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import '../PreviewSection.scss';
import Features from '@utils/features';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import { isJevModel } from '@components/ModelsManagement/jevModel';

interface ConfigurationSectionProps {
  template: any;
  handleStepClick?: (e: any, step: string) => void;
  showEdit?: boolean;
  configDetails?: any;
}

function ConfigurationSection({
  template,
  handleStepClick,
  configDetails,
  showEdit,
}: ConfigurationSectionProps) {
  const [t] = useTranslation('common');
  const profile = template?.profile?.data;
  const model = template?.configuration?.data?.model;
  // For Jev model, disable extended thinking (rationale)
  const hasRationale = isJevModel(model)
    ? false
    : profile?.inference_configuration?.performance_config?.rationale;
  const fileUploadAllowed = profile?.inference_configuration?.file_inference_config?.allowed;
  const fileTypes = profile?.inference_configuration?.file_inference_config?.types;
  const inferenceConfig = template?.profile?.data?.inference_configuration;
  const performanceConfig = inferenceConfig?.performance_config;
  const { userResponse } = useBoomiAgentFoundationContext();
  const isBYOMFeatureEnabled = Features.isAgentGardenByomEnabled(userResponse?.user);
  const getModelText = () => {
    if (performanceConfig?.processing_mode === 'quick') return t('Fast');
    if (performanceConfig?.processing_mode === 'standard') return t('Standard');
    return '';
  };

  const getModelName = () => {
    let processingModeLabel = getModelText();
    if (isJevModel(model)) {
      processingModeLabel = '';
    }
    let modelName = '';
    if (template?.configuration?.data?.model?.display_name) {
      modelName = template.configuration.data?.model?.display_name;
    }
    if (configDetails) {
      modelName = configDetails?.models?.find(
        (m: any) => m.model_id === template.configuration.data?.model?.model_id,
      )?.display_name;
    }
    if (modelName) {
      const displayText = processingModeLabel
        ? `${processingModeLabel} - ${modelName}`
        : `${modelName}`;
      return displayText;
    }
    return '\u2013';
  };
  const getSuffix = () => {
    let suffix = '';
    if (
      isBYOMFeatureEnabled
    ) {
      suffix = (performanceConfig.processing_mode === 'standard' || performanceConfig.processing_mode === 'quick')
        ? ` (${t('boomi_managed')})`
        : ` (${t('self_managed')})`;
    }
    return suffix;
  };
  return (
    <div className="review-agent-container">
      {showEdit && handleStepClick && (
        <div className="preview-button-container">
          <div className="agent-title">
            {t('review_panel_configuration_title') || 'Configuration'}
          </div>
          <ExIconButton
            icon="pencil"
            type={IconButtonType.TERTIARY}
            flavor={IconButtonFlavor.BASE}
            size={IconButtonSize.SMALL}
            tooltipText="Edit"
            onClick={(e: any) => {
              handleStepClick(e, 'Configure');
            }}
          />
        </div>
      )}
      <div className="review-agent-section">
        <div className="info-row">
          <div className="info-key">{t('model') || 'Model'}</div>
          <div className="info-value">
            <div>
              <div>
                <span className="model-name">{getModelName()}</span>
                {isBYOMFeatureEnabled && !isJevModel(model) && (
                  <span className="model-suffix">
                    {getSuffix()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="info-row">
          <div className="info-key">{t('profile_model_config_title')}</div>
          <div className="info-value">
            {t('extended_thinking')}
            :
            {' '}
            {hasRationale ? t('on') : t('off')}
          </div>
        </div>
        <div className="info-row">
          <div className="info-key">File Upload</div>
          <div className="info-value">
            {fileUploadAllowed ? 'On' : 'Off'}
            {fileUploadAllowed && fileTypes && fileTypes.length > 0 && (
              <>
                <br />
                {t('profile_allowed_formats')}
                :
                {' '}
                {fileTypes.join(', ')}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

ConfigurationSection.defaultProps = {
  showEdit: true,
  handleStepClick: undefined,
  configDetails: undefined,
};

export default ConfigurationSection;
